import AuroraBackground from "@/components/layout/aurora-background";
import { Button } from "@/components/ui/button";
import { inquiryPath, localizedPath } from "@/lib/routes";
import { ArrowRight } from "lucide-react";
import { lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";

// The animated blueprint has its own sizeable animation/runtime graph. Loading
// it as a separate chunk keeps it from blocking the headline and primary CTA.
const SignalGrid = lazy(() => import("@/components/layout/signal-grid"));

export default function Hero() {
  const { t } = useTranslation("home");
  const { lng } = useParams<{ lng: string }>();
  const locale = lng ?? "it";

  return (
    <section className="relative overflow-hidden border-b border-border">
      <AuroraBackground className="opacity-70" />

      <div className="mx-auto grid w-full max-w-7xl grid-cols-[minmax(0,1fr)] items-center gap-12 px-4 pb-20 pt-16 sm:px-6 sm:pt-24 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] lg:gap-16 lg:px-8 lg:pb-28">
        {/* Left: editorial headline */}
        <div className="min-w-0">
          <div className="enkey-hero-enter flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
            <span className="text-primary">/ {t("hero.eyebrow")}</span>
            <span className="hidden h-px w-10 bg-border sm:block" />
            <span className="hidden sm:block">{t("hero.location")}</span>
          </div>

          <h1 className="mt-6 font-display text-5xl font-bold uppercase leading-[0.92] tracking-tight text-balance sm:text-6xl lg:text-7xl xl:text-8xl">
            <span className="block">{t("hero.title_1")}</span>
            <span className="block text-primary">
              {t("hero.title_highlight")}
            </span>
            <span className="block">{t("hero.title_2")}</span>
          </h1>

          <p className="enkey-hero-enter enkey-hero-enter-delay-1 mt-7 max-w-xl text-lg text-muted-foreground text-balance">
            {t("hero.subtitle")}
          </p>

          <div className="enkey-hero-enter enkey-hero-enter-delay-2 mt-9 flex flex-col gap-3 sm:flex-row">
            <Button asChild size="lg" className="group h-12 px-8 text-base">
              <Link to={inquiryPath(locale, { requestType: "new_project" })}>
                {t("hero.cta_primary")}
                <ArrowRight className="transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="secondary"
              className="group h-12 px-8 text-base"
            >
              <Link to={localizedPath("portfolio", locale)}>
                {t("hero.cta_secondary")}
                <ArrowRight className="transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Right: technical blueprint panel */}
        <div className="enkey-hero-visual relative min-w-0 w-[calc(100vw-2rem)] sm:w-full">
          <Suspense
            fallback={
              <div
                aria-hidden
                className="aspect-square w-full rounded-xl border border-border bg-secondary/40"
              />
            }
          >
            <SignalGrid />
          </Suspense>
          <span className="absolute -bottom-3 right-3 font-mono text-xs text-muted-foreground">
            01 / 04
          </span>
        </div>
      </div>
    </section>
  );
}
